import {
  ConnectionStatusEnum,
  type MarketAccount,
  type MarketDataCardUnifiedLiquidityMarket,
  type MarketDataSnapshot,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import {
  UnifiedLiquidityMarketTooltipStatus,
  type UnifiedLiquidityMarketTooltipStatus as UnifiedLiquidityMarketTooltipStatusType,
} from '../MarketSelector/unifiedLiquidityMarketTooltip';

export interface UnifiedLiquidityMarketTooltipGroup {
  key: string;
  displayName: string;
  market: string;
  rows: UnifiedLiquidityMarketTooltipRow[];
}

export interface UnifiedLiquidityMarketTooltipRow {
  symbol: string;
  displayName: string;
  status: UnifiedLiquidityMarketTooltipStatusType;
}

interface GetUnifiedLiquidityMarketTooltipGroupsParams {
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  unifiedLiquidityMarketSymbols: MarketDataCardUnifiedLiquidityMarket[];
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
  marketAccountsByName: Map<string, MarketAccount>;
  marketDisplayNameByName: Map<string, string>;
  securitiesBySymbol: Map<string, Security>;
  snapshotMarkets: MarketDataSnapshot['Markets'] | undefined;
}

export function getUnifiedLiquidityMarketTooltipGroups({
  primarySymbol,
  primarySecurity,
  unifiedLiquidityMarketSymbols,
  selectedMarkets,
  selectedMarketAccounts,
  unifiedLiquidityTokens,
  marketAccountsByName,
  marketDisplayNameByName,
  securitiesBySymbol,
  snapshotMarkets,
}: GetUnifiedLiquidityMarketTooltipGroupsParams): UnifiedLiquidityMarketTooltipGroup[] {
  const selectedSymbolsByMarketAccount = getSelectedSymbolsByMarketAccount(unifiedLiquidityMarketSymbols);
  const marketAccountNames = getMarketAccountNamesForTooltip(selectedMarketAccounts);
  const selectedMarketGroups = getSelectedMarketGroups({
    selectedMarkets,
    marketAccountsByName,
    selectedMarketAccounts: marketAccountNames,
  });

  const marketAccountGroups = marketAccountNames
    .map(marketAccountName => {
      const marketAccount = marketAccountsByName.get(marketAccountName);
      if (!marketAccount) {
        return undefined;
      }

      const symbolCandidates = getSymbolCandidatesForMarket({
        market: marketAccount.Market,
        primarySymbol,
        primarySecurity,
        selectedSymbols: selectedSymbolsByMarketAccount.get(marketAccountName),
        unifiedLiquidityTokens,
      });
      if (!symbolCandidates.length) {
        return undefined;
      }

      return {
        key: marketAccountName,
        displayName: marketAccount.DisplayName ?? marketAccountName,
        market: marketAccount.Market,
        rows: symbolCandidates.map(symbol => ({
          symbol,
          displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol,
          status: getSymbolStatus({
            market: marketAccount.Market,
            selectionKey: marketAccountName,
            primarySymbol,
            symbol,
            snapshotMarkets,
          }),
        })),
      };
    })
    .filter((group): group is UnifiedLiquidityMarketTooltipGroup => group != null);

  const marketGroups = selectedMarketGroups
    .map(market => {
      const symbolCandidates = getSymbolCandidatesForMarket({
        market,
        primarySymbol,
        primarySecurity,
        unifiedLiquidityTokens,
      });
      if (!symbolCandidates.length) {
        return undefined;
      }

      return {
        key: market,
        displayName: marketDisplayNameByName.get(market) ?? market,
        market,
        rows: symbolCandidates.map(symbol => ({
          symbol,
          displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol,
          status: getSymbolStatus({
            market,
            selectionKey: market,
            primarySymbol,
            symbol,
            snapshotMarkets,
          }),
        })),
      };
    })
    .filter((group): group is UnifiedLiquidityMarketTooltipGroup => group != null);

  return [...marketAccountGroups, ...marketGroups].toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

function getSelectedSymbolsByMarketAccount(unifiedLiquidityMarketSymbols: MarketDataCardUnifiedLiquidityMarket[]) {
  const symbolsByMarketAccount = new Map<string, Set<string>>();

  for (const selection of unifiedLiquidityMarketSymbols) {
    const symbols = symbolsByMarketAccount.get(selection.marketAccount) ?? new Set<string>();
    symbols.add(selection.symbol);
    symbolsByMarketAccount.set(selection.marketAccount, symbols);
  }

  return symbolsByMarketAccount;
}

function getMarketAccountNamesForTooltip(selectedMarketAccounts: string[]) {
  return Array.from(new Set(selectedMarketAccounts));
}

function getSelectedMarketGroups({
  selectedMarkets,
  selectedMarketAccounts,
  marketAccountsByName,
}: {
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  marketAccountsByName: Map<string, MarketAccount>;
}) {
  const selectedMarketAccountMarkets = new Set(
    selectedMarketAccounts
      .map(marketAccountName => marketAccountsByName.get(marketAccountName)?.Market)
      .filter((market): market is string => market != null)
  );

  return Array.from(new Set(selectedMarkets)).filter(market => !selectedMarketAccountMarkets.has(market));
}

function getSymbolCandidatesForMarket({
  market,
  primarySymbol,
  primarySecurity,
  selectedSymbols,
  unifiedLiquidityTokens,
}: {
  market: string;
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  selectedSymbols?: Set<string>;
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
}) {
  return Array.from(
    new Set([
      ...(primarySymbol && primarySecurity?.Markets?.includes(market) ? [primarySymbol] : []),
      ...unifiedLiquidityTokens.filter(token => token.Market === market).map(token => token.Symbol),
      ...(selectedSymbols ? Array.from(selectedSymbols) : []),
    ])
  ).toSorted((a, b) => a.localeCompare(b));
}

function getMarketSnapshot({
  market,
  selectionKey,
  snapshotMarkets,
}: {
  market: string;
  selectionKey: string;
  snapshotMarkets: MarketDataSnapshot['Markets'] | undefined;
}) {
  return snapshotMarkets?.[market] ?? snapshotMarkets?.[selectionKey];
}

function getSymbolStatus({
  market,
  selectionKey,
  primarySymbol,
  symbol,
  snapshotMarkets,
}: {
  market: string;
  selectionKey: string;
  primarySymbol: string | undefined;
  symbol: string;
  snapshotMarkets: MarketDataSnapshot['Markets'] | undefined;
}) {
  const marketSnapshot = getMarketSnapshot({ market, selectionKey, snapshotMarkets });

  if (marketSnapshot?.Status === ConnectionStatusEnum.Unavailable) {
    return UnifiedLiquidityMarketTooltipStatus.Unavailable;
  }

  if (marketSnapshot?.Symbols) {
    return marketSnapshot.Symbols.includes(symbol)
      ? UnifiedLiquidityMarketTooltipStatus.Enabled
      : UnifiedLiquidityMarketTooltipStatus.Disabled;
  }

  if (marketSnapshot) {
    return symbol === primarySymbol
      ? UnifiedLiquidityMarketTooltipStatus.Enabled
      : UnifiedLiquidityMarketTooltipStatus.Disabled;
  }

  return UnifiedLiquidityMarketTooltipStatus.Disabled;
}
